import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './AnimatedGraphicsChart.css';

const AnimatedGraphicsChart = ({ data, height = 650 }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) return;
    
    // Prevent unnecessary re-renders
    const dataString = JSON.stringify(data);
    if (chartRef.current && chartRef.current.dataset.lastData === dataString) return;
    
    try {

    // Sort data by duration
    const sortedData = Object.entries(data).sort(([,a], [,b]) => b - a);
    const maxDuration = Math.max(...Object.values(data));

    // Create chart HTML
    const formatHoursMinutes = (secondsVal) => {
      if (isNaN(secondsVal) || secondsVal < 0) return '0h 0m';
      const totalMinutes = Math.round(secondsVal / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    };

    const generateBarsHTML = () => {
      return sortedData.map(([graphicName, duration]) => {
        const percentage = (duration / maxDuration) * 100;
        const durationText = formatHoursMinutes(duration);

        return `
          <div class="bar-item">
            <div class="bar-label">${graphicName}</div>
            <div class="bar-wrapper">
              <div class="bar-fill" data-percentage="${percentage.toFixed(1)}">
                <div class="bar-percentage">${durationText}</div>
              </div>
            </div>
            <div class="bar-value">${durationText}</div>
          </div>
        `;
      }).join('');
    };

    if (chartRef.current) {
      // Calculate proper height based on number of bars
      const barHeight = 52; // Height of each bar item (including gap)
      const baseHeight = 200; // Base height for padding, headers, etc.
      const calculatedHeight = Math.max(height, baseHeight + (sortedData.length * barHeight));
      
      chartRef.current.style.height = calculatedHeight + 'px';
      
      chartRef.current.innerHTML = `
        <div class="container">
          <div class="bar-container">
            ${generateBarsHTML()}
          </div>
        </div>
      `;

      // Initialize GSAP animations with improved performance
      const initializeChart = () => {
        const bars = Array.from(chartRef.current.querySelectorAll('.bar-item'));
        const fills = Array.from(chartRef.current.querySelectorAll('.bar-fill'));
        
        // Set initial state for better performance
        gsap.set(bars, { opacity: 0, x: -30, scale: 0.95 });
        gsap.set(fills, { width: 0 });
        
        // Animate bars appearing with smoother easing
        gsap.to(bars, {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.08,
          ease: "power2.out",
          clearProps: "scale"
        });

        // Animate bar fills with improved timing
        gsap.to(fills, {
          width: (i, target) => target.getAttribute('data-percentage') + '%',
          duration: 1.5,
          delay: 0.4,
          stagger: 0.08,
          ease: "power3.out"
        });
      };

      // Store the data string to prevent unnecessary re-renders
      chartRef.current.dataset.lastData = dataString;
      
      // Use requestAnimationFrame for smoother initialization
      requestAnimationFrame(() => {
        setTimeout(initializeChart, 150);
      });
    }

    } catch (error) {
      console.error('Error in AnimatedGraphicsChart:', error);
    }
  }, [data, height]);

  return (
    <div 
      ref={chartRef} 
      className="animated-graphics-chart"
      style={{ 
        height: height + 'px',
        transition: 'height 0.8s ease'
      }}
    />
  );
};

export default AnimatedGraphicsChart; 